import type { Signal } from "@lit-labs/signals";
import type { IRpcService } from "../rpc/types";
import type { CreatorItem } from "../creator-select/types";
import type { GetCoPromotersForEntityResult } from "api-contracts/rpc/co-promotion";
import type { ExistingCoPromoter } from "./save-collaborators";

interface CoPromoterTargetState<T extends { showCoPromotion: boolean }> {
  uiToggles: Signal.State<T>;
  collaboratorHandles: Signal.State<string[]>;
  existingCollaborators: Signal.State<CreatorItem[]>;
}

export interface LoadCoPromotersResult {
  loadedCoPromoters: ExistingCoPromoter[];
}

export async function loadCoPromoters<T extends { showCoPromotion: boolean }>(
  rpcService: IRpcService,
  entityType: string,
  entityId: string,
  state: CoPromoterTargetState<T>,
): Promise<LoadCoPromotersResult> {
  const result = await rpcService.call<GetCoPromotersForEntityResult>("get-co-promoters-for-entity", {
    entityType,
    entityId,
  });

  if (result.coPromoters.length > 0) {
    state.uiToggles.set({ ...state.uiToggles.get(), showCoPromotion: true } as T);
    const handles = result.coPromoters.map((cp) => cp.promoterHandle).filter((h): h is string => h !== null);
    state.collaboratorHandles.set(handles);
    state.existingCollaborators.set(
      result.coPromoters.map((cp) => ({
        id: cp.promoterId,
        handle: cp.promoterHandle,
        imageUrl: cp.promoterAvatarUrl ?? undefined,
      })),
    );
    return {
      loadedCoPromoters: result.coPromoters
        .filter((cp) => cp.promoterHandle !== null)
        .map((cp) => ({ handle: cp.promoterHandle!, coPromotionId: cp.coPromotionId })),
    };
  }

  return { loadedCoPromoters: [] };
}
